import { Controller } from "stimulus";
import { Formio } from "formiojs";
import FormioUtils from "formiojs/utils";

export default class extends Controller {
  static targets = [
    "form",
    "formSection",
    "questionArea",
    "agreement",
    "collapseIcon",
    "responsesInput",
    "schemaJson",
    "responsesJson",
    "graderResponsesJson",
    "adjudicationSelectedGrader",
    "railsForm",
  ];
  static values = {
    readOnly: Boolean,
  };

  async connect() {
    const schema = this.parseSchema();
    if (!schema.components || schema.components.length === 0) {
      this.formTarget.innerHTML =
        '<p class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">No Form.io components found for this step. Edit the grading form under Trial settings → Modalities.</p>';
      return;
    }

    try {
      this.formio = await Formio.createForm(this.formTarget, schema, {
        readOnly: this.readOnlyValue,
      });

      const data = this.parseResponses();
      if (data && Object.keys(data).length) {
        await this.formio.setSubmission({ data }, { noValidate: true, noCheck: true });
      }

      this.graderResponses = this.parseGraderResponses();
      if (this.graderResponses.length > 0) {
        this.decorateAdjudicationGraderAnswers(schema);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Prescreen Form.io failed to load", error);
      this.formTarget.innerHTML =
        '<p class="text-sm text-red-800 bg-red-50 border border-red-200 rounded p-3">Could not load the grading form. Refresh the page or contact support.</p>';
    }
  }

  parseSchema() {
    if (!this.hasSchemaJsonTarget) {
      return { components: [] };
    }

    try {
      const raw = JSON.parse(this.schemaJsonTarget.textContent);
      if (raw.components) {
        return raw;
      }
      if (raw.form && raw.form.components) {
        return raw.form;
      }
      return { components: [] };
    } catch (_error) {
      return { components: [] };
    }
  }

  parseResponses() {
    if (!this.hasResponsesJsonTarget) {
      return {};
    }

    try {
      return JSON.parse(this.responsesJsonTarget.textContent) || {};
    } catch (_error) {
      return {};
    }
  }

  parseGraderResponses() {
    if (!this.hasGraderResponsesJsonTarget) {
      return [];
    }

    try {
      const parsed = JSON.parse(this.graderResponsesJsonTarget.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  decorateAdjudicationGraderAnswers(schema) {
    FormioUtils.eachComponent(this.formio.components, (componentInstance) => {
      if (!componentInstance?.component?.key || !componentInstance.element) {
        return;
      }

      if (componentInstance.element.classList.contains("formio-hidden")) {
        return;
      }

      const questionKey = componentInstance.component.key;
      const host = componentInstance.element.closest(".formio-component") || componentInstance.element;

      if (host.querySelector(".prescreen-grader-answers-div")) {
        return;
      }

      const answersDiv = document.createElement("div");
      answersDiv.className = "prescreen-grader-answers-div";

      let hasAnyAnswer = false;
      this.graderResponses.forEach((graderEntry, index) => {
        const responseData = graderEntry.responses?.data || {};
        if (!Object.prototype.hasOwnProperty.call(responseData, questionKey)) {
          return;
        }

        hasAnyAnswer = true;
        const rawValue = responseData[questionKey];
        const displayValue = this.displayValueForQuestion(schema, questionKey, rawValue);
        const graderLabel =
          graderEntry.grader_label || `Grader ${graderEntry.grader_index || index + 1}`;

        const line = document.createElement("div");
        line.className = "prescreen-grader-answer-line";
        line.textContent = `${graderLabel}: ${displayValue}`;

        if (!this.readOnlyValue) {
          line.classList.add("prescreen-grader-answer-line--clickable");
          line.dataset.questionKey = questionKey;
          line.dataset.answer = rawValue === null || rawValue === undefined ? "" : String(rawValue);
          line.dataset.graderIndex = String(graderEntry.grader_index || index + 1);
          line.addEventListener("click", (event) => this.applyGraderAnswer(event));
        }

        answersDiv.appendChild(line);
      });

      if (!hasAnyAnswer) {
        return;
      }

      host.classList.add("prescreen-formio-question-with-grader-answers");
      componentInstance.element.appendChild(answersDiv);

      this.highlightIfGradersDisagree(host, questionKey);
    });
  }

  highlightIfGradersDisagree(host, questionKey) {
    const values = this.graderResponses
      .map((entry) => entry.responses?.data?.[questionKey])
      .filter((value) => value !== undefined && value !== null && value !== "");

    if (values.length > 1 && !values.every((value) => value === values[0])) {
      host.classList.add("prescreen-formio-question--conflict");
    }
  }

  displayValueForQuestion(schema, questionKey, questionValue) {
    if (questionValue === null || questionValue === undefined || questionValue === "") {
      return "—";
    }

    const label = this.labelFromSchema(schema, questionKey, questionValue);
    if (Array.isArray(label)) {
      return label.join(", ");
    }
    return label;
  }

  labelFromSchema(schema, questionKey, questionValue) {
    const component = this.findComponentByKey(schema?.components || [], questionKey);
    if (!component) {
      return String(questionValue);
    }

    if (component.type === "radio" && Array.isArray(component.values)) {
      const match = component.values.find((item) => String(item.value) === String(questionValue));
      if (match?.label) {
        return match.label;
      }
    }

    if (component.type === "select" && component.data?.values) {
      const match = component.data.values.find((item) => String(item.value) === String(questionValue));
      if (match?.label) {
        return match.label;
      }
    }

    return String(questionValue);
  }

  findComponentByKey(components, questionKey) {
    for (const component of components) {
      if (component?.key === questionKey) {
        return component;
      }
      if (component?.components?.length) {
        const nested = this.findComponentByKey(component.components, questionKey);
        if (nested) {
          return nested;
        }
      }
    }
    return null;
  }

  applyGraderAnswer(event) {
    const line = event.currentTarget;
    const questionKey = line.dataset.questionKey;
    const answer = line.dataset.answer;
    const graderIndex = line.dataset.graderIndex;

    if (!questionKey) {
      return;
    }

    FormioUtils.eachComponent(this.formio.components, (componentInstance) => {
      if (componentInstance.component?.key !== questionKey) {
        return;
      }

      if (componentInstance.setValue) {
        componentInstance.setValue(answer);
      } else {
        const input = componentInstance.element?.querySelector("input, textarea, select");
        if (input) {
          input.value = answer;
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    });

    if (this.hasAdjudicationSelectedGraderTarget && graderIndex) {
      this.adjudicationSelectedGraderTarget.value = graderIndex;
    }
  }

  toggleSubmitArea() {
    if (!this.hasQuestionAreaTarget) {
      return;
    }

    const hidden = this.questionAreaTarget.classList.toggle("hidden");
    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.classList.toggle("fa-minus-square-o", !hidden);
      this.collapseIconTarget.classList.toggle("fa-plus-square-o", hidden);
    }
  }

  async formCommit(event) {
    event.preventDefault();
    if (this.readOnlyValue) {
      return;
    }

    if (this.hasAgreementTarget && !this.agreementTarget.checked) {
      window.alert("Please acknowledge the accuracy statement before submitting.");
      return;
    }

    await this.persistAndSubmit();
  }

  async submit(event) {
    if (this.readOnlyValue) {
      event.preventDefault();
      return;
    }

    if (this._bypassValidation) {
      this._bypassValidation = false;
      return;
    }

    event.preventDefault();
    await this.persistAndSubmit();
  }

  async persistAndSubmit() {
    if (!this.formio) {
      return;
    }

    const valid = await this.formio.checkValidity(this.formio.data, true);
    if (!valid) {
      return;
    }

    this.responsesInputTarget.value = JSON.stringify(this.formio.data || {});
    this._bypassValidation = true;
    this.railsFormTarget.requestSubmit();
  }
}
